/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { NumberFormatValues, NumericFormat } from "react-number-format";
import { FlexContainer, Message, Spacer, Text, theme } from "@nordcloud/gnui";
import { useCurrency } from "~/hooks";
import { formatMoney, getCurrencySymbol, isNotNil } from "~/tools";

type Props = {
  budget: number | undefined;
  setBudget: (value: number | undefined) => void;
  lastTwelveMonthsSpend: number;
};

export function SetupBudget({
  budget,
  setBudget,
  lastTwelveMonthsSpend,
}: Props) {
  const { currency } = useCurrency();

  const prefix = getCurrencySymbol(currency);

  const onValueChange = ({ floatValue }: NumberFormatValues) =>
    setBudget(floatValue);

  return (
    <>
      <FlexContainer
        direction="column"
        alignItems="start"
        data-testid="setup-budget"
      >
        <Text>
          Yearly Budget{" "}
          <span style={{ color: theme.colors.statusDanger[0] }}>*</span>
        </Text>
        <NumericFormat
          prefix={isNotNil(budget) ? `${prefix} ` : ""}
          value={budget}
          displayType="input"
          allowNegative={false}
          data-testid="setup-budget-input"
          css={{
            border: `1px solid ${theme.color.border.input}`,
            width: "100%",
            boxSizing: "border-box",
            background: "transparent",
            backgroundColor: `${theme.color.field.default}`,
            fontFamily: `${theme.typography.fonts.body}`,
            fontSize: `${theme.fontSizes.md}`,
            borderRadius: `${theme.radiusDefault}`,
            padding: `${theme.spacing.spacing02}`,
            margin: "3px 0 2px",
            color: `${theme.color.text.text01}`,
          }}
          placeholder={
            isNotNil(budget)
              ? `${budget} %`
              : `e.g. ${formatMoney("200000", currency)}`
          }
          onValueChange={onValueChange}
        />
      </FlexContainer>
      <Spacer height={theme.spacing.spacing03} />
      <Message status="notification" image="info" width="100%">
        <Text
          weight="medium"
          color={theme.color.text.info}
          mb={theme.spacing.spacing00}
        >
          Spend for the last 12 months:{" "}
          {formatMoney(lastTwelveMonthsSpend, currency, false)}
        </Text>
      </Message>
    </>
  );
}
